import React, { useMemo } from 'react';
import { RewindStats } from '../utils/rewindStats';
import CustomCruiseIcon from './CustomCruiseIcon';
import { parseISO } from 'date-fns';

interface ShareRewindImageStoriesProps {
  year: number;
  stats: RewindStats;
}

const ShareRewindImageStories = React.forwardRef<HTMLDivElement, ShareRewindImageStoriesProps>(
  ({ year, stats }, ref) => {
    // Prioritize ships for shareable image: new ships first, then most recent sailings
    const prioritizedShipImages = useMemo(() => {
      const sorted = [...stats.shipImages].sort((a, b) => {
        // First priority: new ships come first
        if (a.isNewShip && !b.isNewShip) return -1;
        if (!a.isNewShip && b.isNewShip) return 1;
        
        // Second priority: most recent return dates (closer to end of year)
        const dateA = parseISO(a.returnDate);
        const dateB = parseISO(b.returnDate);
        return dateB.getTime() - dateA.getTime(); // Descending order (most recent first)
      });
      
      return sorted.slice(0, 9); // Limit to 9 for collage
    }, [stats.shipImages]);
    
    const hasShipImages = prioritizedShipImages.length > 0 && prioritizedShipImages.some(img => img.imageUrl);

    return (
      <div
        ref={ref}
        data-share-image="true"
        className="relative mx-auto"
        style={{
          width: '1080px',
          height: '1920px',
          fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
          boxSizing: 'border-box',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div className="relative w-full h-full bg-gradient-to-b from-purple-900 via-pink-800 to-orange-700 overflow-hidden">
          {/* Background Ship Collage */}
          {hasShipImages && (
            <div className="absolute inset-0">
              <div className="grid grid-cols-3 h-full w-full">
                {Array.from({ length: 9 }).map((_, index) => {
                  const shipImage = prioritizedShipImages[index];
                  if (shipImage?.imageUrl) {
                    return (
                      <div key={index} className="relative overflow-hidden">
                        <img
                          src={shipImage.imageUrl}
                          alt=""
                          className="w-full h-full object-cover opacity-30"
                          crossOrigin="anonymous"
                        />
                      </div>
                    );
                  }
                  return (
                    <div key={index} className="bg-gradient-to-br from-purple-800/20 to-pink-800/20" />
                  );
                })}
              </div>
              <div className="absolute inset-0" style={{
                background: 'linear-gradient(to bottom, rgba(88, 28, 135, 0.4) 0%, rgba(147, 51, 234, 0.5) 30%, rgba(219, 39, 119, 0.5) 70%, rgba(234, 88, 12, 0.6) 100%)'
              }}></div>
            </div>
          )}

          {/* Decorative Elements */}
          <div className="absolute top-20 right-20 w-32 h-32 bg-white/5 rounded-full"></div>
          <div className="absolute bottom-20 left-20 w-24 h-24 bg-white/5 rounded-full"></div>
          <div className="absolute top-1/2 right-10 w-16 h-16 bg-white/5 rounded-full"></div>

          {/* Content */}
          <div className="relative z-10 flex flex-col h-full px-16">
            {/* Top Section - Title */}
            <div className="flex flex-col items-center justify-center text-center pt-20 pb-8">
              <div className="mb-6">
                <CustomCruiseIcon size={100} className="text-white" />
              </div>
              <div className="text-7xl font-bold text-white leading-none mb-4">
                Cruising Along
              </div>
              <div className="text-8xl font-bold text-white leading-none mb-2">
                Rewind {year}
              </div>
              <div className="text-5xl text-white/90 font-medium">
                Your Year in Review
              </div>
            </div>

            {/* Middle Section - Statistics */}
            <div className="flex-1 flex flex-col items-center justify-center space-y-8">
              {/* Cruises */}
              <div className="text-center">
                <div className="text-[12rem] font-bold text-white leading-none mb-4">
                  {stats.cruisesCompleted}
                </div>
                <div className="text-6xl text-white/90 font-medium">
                  {stats.cruisesCompleted === 1 ? 'Cruise' : 'Cruises'} in {year}
                </div>
              </div>

              {/* Nights at Sea */}
              <div className="text-center">
                <div className="text-[10rem] font-bold text-white leading-none mb-4">
                  {stats.nightsAtSea}
                </div>
                <div className="text-6xl text-white/90 font-medium">
                  {stats.nightsAtSea === 1 ? 'Night' : 'Nights'} at Sea
                </div>
              </div>

              {/* First Time Items Grid - Only show if applicable */}
              {(stats.firstTimeCruiseLines.length > 0 || stats.firstTimeShips.length > 0 || stats.firstTimePorts.length > 0) && (
                <div className="grid grid-cols-3 gap-6 w-full mt-8">
                  {stats.firstTimeCruiseLines.length > 0 && (
                    <div className="text-center">
                      <div className="text-7xl font-bold text-white mb-2">
                        {stats.firstTimeCruiseLines.length}
                      </div>
                      <div className="text-4xl text-white/90">
                        New Cruise {stats.firstTimeCruiseLines.length === 1 ? 'Line' : 'Lines'} Sailed
                      </div>
                    </div>
                  )}
                  {stats.firstTimeShips.length > 0 && (
                    <div className="text-center">
                      <div className="text-7xl font-bold text-white mb-2">
                        {stats.firstTimeShips.length}
                      </div>
                      <div className="text-4xl text-white/90">
                        First Time {stats.firstTimeShips.length === 1 ? 'Ship' : 'Ships'} Sailed
                      </div>
                    </div>
                  )}
                  {stats.firstTimePorts.length > 0 && (
                    <div className="text-center">
                      <div className="text-7xl font-bold text-white mb-2">
                        {stats.firstTimePorts.length}
                      </div>
                      <div className="text-4xl text-white/90">
                        First Time {stats.firstTimePorts.length === 1 ? 'Port' : 'Ports'} Visited
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Bottom Section - Branding */}
            <div className="flex flex-col items-center text-center pb-12">
              <div className="text-5xl font-bold text-white leading-none mb-2">
                Cruising Along
              </div>
              <div className="text-4xl text-white/90 leading-none mb-4">
                Track Your Cruise Adventures
              </div>
              <div 
                style={{ 
                  fontSize: '2rem',
                  color: '#fbbf24',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '9999px',
                  textAlign: 'center',
                  display: 'inline-block',
                  width: 'fit-content',
                  margin: '0 auto',
                  whiteSpace: 'nowrap',
                  lineHeight: '1',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                keep.cruisingalong.com
              </div>
            </div>
          </div>

          {/* Bottom Accent */}
          <div className="absolute bottom-0 left-0 right-0 h-3 bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400"></div>
        </div>
      </div>
    );
  }
);

ShareRewindImageStories.displayName = 'ShareRewindImageStories';

export default ShareRewindImageStories;

